'use client';

import type { IconAnimateMode } from '@magicschool/spellbook/components/Icon';
import { Text } from '@magicschool/spellbook/components/Text';
import {
  ToggleButtonLeadingIconSlot,
  ToggleButtonSpinnerSlot,
  ToggleButtonTrailingIconSlot,
} from '@magicschool/spellbook/components/ToggleButton/slots';
import { useAnimatedIcon } from '@magicschool/spellbook/hooks/useAnimatedIcon';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { getButtonTextVariant } from '@magicschool/spellbook/lib/controls';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as TogglePrimitive from 'radix-ui/toggle';
import { memo } from 'react';

export type ToggleButtonProps = React.ComponentProps<typeof TogglePrimitive.Root> &
  VariantProps<typeof toggleButtonVariants> & {
    /** If true, the button will display a loading spinner icon and click functionality will be disabled. */
    isLoading?: boolean;
    /** Animate the leading/trailing icon on hover and focus of the button. `'hover'` plays on hover or focus; `'mount'` plays on mount; `false` explicitly disables. Only valid for `AnimatedIconName` values. */
    animate?: IconAnimateMode;
  };

export const toggleButtonVariants = cva(
  [
    'cursor-pointer',
    'inline-flex',
    'items-center',
    'justify-center',
    'whitespace-nowrap',
    'transition-colors',
    'shrink-0',
    '[&_svg]:shrink-0',
    'outline-none',
    'focus-visible-ring',
    'border-1',
    'border-transparent',
    'overflow-hidden',
    'text-ellipsis',
    'aria-busy:pointer-events-none',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:bg-transparent',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:text-disabled-foreground',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:border-transparent',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:shadow-none',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:transition-none',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:cursor-not-allowed',
    '[&[aria-disabled="true"]:not([aria-busy="true"])]:[&_svg]:text-disabled-foreground',
    'data-[state=on]:[&[aria-disabled="true"]:not([aria-busy="true"])]:border-disabled-background',
    'data-[state=on]:[&[aria-disabled="true"]:not([aria-busy="true"])]:bg-disabled-background',
    'data-[state=on]:[&[aria-disabled="true"]:not([aria-busy="true"])]:text-disabled-foreground',
    'aria-pressed:[&[aria-disabled="true"]:not([aria-busy="true"])]:border-disabled-background',
    'aria-pressed:[&[aria-disabled="true"]:not([aria-busy="true"])]:bg-disabled-background',
    'aria-pressed:[&[aria-disabled="true"]:not([aria-busy="true"])]:text-disabled-foreground',
  ],
  {
    variants: {
      /** The visual appearance of the button. */
      variant: {
        default: ['text-default-text', '[&_svg]:text-default-icon', 'active:text-default-text-strong'],
      },
      /** The size of the button. */
      size: {
        xs: ['h-control-h-xs', 'gap-0', 'px-1', 'py-1', 'min-w-7'],
        sm: ['h-control-h-sm', 'gap-0.5', 'px-1.5', 'py-1', 'min-w-7'],
        md: ['h-control-h-md', 'gap-1', 'px-2', 'py-2', 'min-w-14'],
        lg: ['h-control-h-lg', 'gap-1', 'px-3', 'py-3', 'min-w-20'],
      },
      /** The radius shape of the button. */
      shape: {
        default: '',
        circle: 'rounded-full',
        square: 'rounded-none',
      },
      /** Whether the button should take up the full width of its container. */
      fullWidth: {
        true: 'w-full',
        false: '',
      },
      /** The alignment of the button's content. */
      justify: {
        start: 'justify-start',
        center: 'justify-center',
        end: 'justify-end',
      },
      /** Whether or not the button has a border. */
      withBorder: {
        true: [
          'bg-surface-background-0',
          'hover:bg-surface-background-2',
          'active:bg-surface-background-3',
          'border-border-strong',
          'hover:border-border-strong-hover',
          'active:border-border-strong-hover',
          '[&[aria-disabled="true"]:not([aria-busy="true"])]:border-disabled-background',
        ],
        false: ['border-transparent', 'bg-surface-transparent-0', 'hover:bg-surface-transparent-1', 'active:bg-surface-transparent-2'],
      },
      icon: {
        true: ['aspect-square', 'p-0', 'min-w-0'],
      },
      color: {
        primary: '',
        success: '',
      },
    },
    compoundVariants: [
      {
        className: 'rounded-control-sm',
        size: ['xs', 'sm'],
        shape: 'default',
      },
      {
        className: 'rounded-control-md',
        size: 'md',
        shape: 'default',
      },
      {
        className: 'rounded-control-lg',
        size: 'lg',
        shape: 'default',
      },
      {
        variant: 'default',
        color: 'primary',
        className: [
          'data-[state=on]:bg-selected-default-background',
          'data-[state=on]:hover:bg-selected-default-background-hover',
          'data-[state=on]:active:bg-selected-default-background-active',
          'data-[state=on]:[&_svg]:text-selected-default-icon',
          'data-[state=on]:hover:[&_svg]:text-selected-default-icon-hover',
          'data-[state=on]:active:[&_svg]:text-selected-default-icon-active',
          'data-[state=on]:text-selected-default-foreground',
          'data-[state=on]:hover:text-selected-default-foreground-hover',
          'data-[state=on]:active:text-selected-default-foreground-active',
          'aria-pressed:bg-selected-default-background',
          'aria-pressed:hover:bg-selected-default-background-hover',
          'aria-pressed:active:bg-selected-default-background-active',
          'aria-pressed:text-selected-default-foreground',
          'aria-pressed:hover:text-selected-default-foreground-hover',
          'aria-pressed:active:text-selected-default-foreground-active',
          'aria-pressed:[&_svg]:text-selected-default-icon',
          'aria-pressed:hover:[&_svg]:text-selected-default-icon-hover',
          'aria-pressed:active:[&_svg]:text-selected-default-icon-active',
        ],
      },
      {
        variant: 'default',
        color: 'primary',
        withBorder: true,
        className: [
          'data-[state=on]:border-selected-default-stroke',
          'data-[state=on]:hover:border-selected-default-stroke-hover',
          'data-[state=on]:active:border-selected-default-stroke-active',
          'aria-pressed:border-selected-default-stroke',
          'aria-pressed:hover:border-selected-default-stroke-hover',
          'aria-pressed:active:border-selected-default-stroke-active',
        ],
      },
      {
        variant: 'default',
        color: 'success',
        className: [
          'data-[state=on]:bg-selected-success-background',
          'data-[state=on]:hover:bg-selected-success-background-hover',
          'data-[state=on]:active:bg-selected-success-background-hover',
          'data-[state=on]:[&_svg]:text-selected-success-icon',
          'data-[state=on]:hover:[&_svg]:text-selected-success-icon-hover',
          'data-[state=on]:active:[&_svg]:text-selected-success-icon-active',
          'data-[state=on]:text-selected-success-foreground',
          'data-[state=on]:hover:text-selected-success-foreground-hover',
          'data-[state=on]:active:text-selected-success-foreground-active',
          'aria-pressed:bg-selected-success-background',
          'aria-pressed:hover:bg-selected-success-background-hover',
          'aria-pressed:active:bg-selected-success-background-hover',
          'aria-pressed:text-selected-success-foreground',
          'aria-pressed:hover:text-selected-success-foreground-hover',
          'aria-pressed:active:text-selected-success-foreground-active',
          'aria-pressed:[&_svg]:text-selected-success-icon',
          'aria-pressed:hover:[&_svg]:text-selected-success-icon-hover',
          'aria-pressed:active:[&_svg]:text-selected-success-icon-active',
        ],
      },
      {
        variant: 'default',
        color: 'success',
        withBorder: true,
        className: [
          'data-[state=on]:border-selected-success-stroke',
          'data-[state=on]:hover:border-selected-success-stroke-hover',
          'data-[state=on]:active:border-selected-success-stroke-active',
          'aria-pressed:border-selected-success-stroke',
          'aria-pressed:hover:border-selected-success-stroke-hover',
          'aria-pressed:active:border-selected-success-stroke-active',
        ],
      },
    ],
  },
);

type ToggleButtonSlottedContentProps = {
  slots: {
    loadingIcon?: React.ReactNode;
    leadingIcon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
  };
  rest?: React.ReactNode;
  size?: ToggleButtonProps['size'];
  isLoading?: boolean;
};

const ToggleButtonSlottedContent = memo<ToggleButtonSlottedContentProps>(({ slots, rest, size, isLoading }) => {
  const textVariant = getButtonTextVariant(size);
  return (
    <>
      {isLoading ? slots.loadingIcon : slots.leadingIcon}
      <Text variant={textVariant} className="px-1" element="span">
        {rest}
      </Text>
      {slots.trailingIcon}
    </>
  );
});

const ToggleButtonBase: React.FC<ToggleButtonProps> = ({
  animate,
  children,
  className,
  color = 'primary',
  disabled,
  fullWidth = false,
  isLoading = false,
  withBorder = false,
  defaultPressed,
  pressed,
  onPressedChange,
  onClick,
  justify = 'center',
  variant = 'default',
  size = 'md',
  shape = 'default',
  ...props
}) => {
  const isActuallyDisabled = disabled || isLoading;
  const resolvedAnimate = isActuallyDisabled ? undefined : animate;
  const { animating, getInteractProps } = useAnimatedIcon(resolvedAnimate);

  const iconSlotProps = { size, animate: resolvedAnimate, animating };

  const { slots, rest } = useSlots(children, {
    loadingIcon: { Component: ToggleButtonSpinnerSlot, props: { size } },
    leadingIcon: { Component: ToggleButtonLeadingIconSlot, props: iconSlotProps },
    trailingIcon: { Component: ToggleButtonTrailingIconSlot, props: iconSlotProps },
  });

  const handleOnPressedChange = (pressed: boolean) => {
    onPressedChange?.(pressed);
  };

  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isActuallyDisabled) {
      // explicitly prevent default and stop propagation
      event.preventDefault();
      event.stopPropagation();

      return;
    }

    onClick?.(event);
  };

  return (
    <TogglePrimitive.Root
      data-spellbook-name="toggle-button"
      data-border={withBorder}
      type="button"
      className={cn(toggleButtonVariants({ variant, size, fullWidth, shape, justify, withBorder, color }), className)}
      defaultPressed={defaultPressed}
      pressed={pressed}
      onPressedChange={handleOnPressedChange}
      onClick={handleOnClick}
      aria-disabled={isActuallyDisabled}
      aria-busy={isLoading}
      {...props}
      {...getInteractProps(props)}
    >
      <ToggleButtonSlottedContent slots={slots} rest={rest} size={size} isLoading={isLoading} />
    </TogglePrimitive.Root>
  );
};

/**
 * Toggle button with visible text label for binary on/off states. Provides `aria-pressed` for accessibility and visual feedback for selected state. Use when the toggle action benefits from a text label.
 *
 * ### When to use
 * - For toggle actions that benefit from visible text (e.g., "Web Search", "Text Support")
 * - For toolbar or action bar toggles where space permits a label
 * - For settings or modes that toggle on/off with immediate visual feedback
 * - When pairing with `ToggleIconButton` for responsive layouts (text on desktop, icon-only on mobile)
 *
 * ### When NOT to use
 * - For icon-only toggles: use `ToggleIconButton` instead
 * - For one-time actions without toggle state: use `Button` instead
 * - For settings that persist after leaving the page: use `Switch` instead — `Switch` is for settings, `ToggleButton` is for modes/actions
 * - For navigation: use `Link` or `Button` with `asChild`
 *
 * ### API
 * - `pressed`: Controlled pressed state — `true` for on, `false` for off
 * - `defaultPressed`: Initial pressed state for uncontrolled usage
 * - `onPressedChange`: Callback when pressed state changes — receives new `boolean` state
 * - `variant`: Visual style — only `default` is available
 * - `color`: Semantic color when pressed: `primary` (default), `success`
 * - `size`: Button dimensions: `xs`, `sm`, `md` (default), `lg`
 * - `shape`: Border radius: `default` (rounded), `circle` (fully rounded), `square` (no rounding)
 * - `withBorder`: Adds border styling — useful for button groups or increased visual distinction
 * - `fullWidth`: Expands button to fill container width
 * - `justify`: Content alignment: `start`, `center` (default), `end`
 * - `isLoading`: Shows spinner, disables clicks, and sets `aria-busy="true"`
 *
 * ### Composition (slots)
 * - `<ToggleButton.LeadingIcon>`: Icon before text. Pass `name` prop with a Lucide icon name. Only include when adding an icon — not rendered by default. Useful for showing state (e.g., check icon when pressed).
 * - `<ToggleButton.TrailingIcon>`: Icon after text. Pass `name` prop with a Lucide icon name. Only include when adding an icon — not rendered by default.
 * - `<ToggleButton.Spinner>`: Loading indicator. Rendered automatically when `isLoading={true}` — no need to include explicitly. Replaces `LeadingIcon` during loading.
 *
 * ### Visual states
 * - **Unpressed**: Default appearance with subtle background
 * - **Pressed**: Background fill and text color change to indicate active/on state; `aria-pressed="true"` set automatically via Radix Toggle
 * - **Hover**: Background shifts to indicate interactivity
 * - **Loading**: Spinner replaces leading icon, pointer events blocked, `aria-busy="true"` set
 * - **Disabled**: Text and icons use disabled color, cursor shows not-allowed, `aria-disabled="true"` set
 * - **Focus**: Focus ring appears around button for keyboard navigation
 *
 * ### Accessibility
 * - Automatically sets `aria-pressed` via Radix Toggle primitive — announces "pressed" or "not pressed" to screen readers
 * - Uses `aria-disabled` instead of `disabled` attribute to maintain focusability for screen readers. Pass `disabled` prop and the component handles `aria-disabled` and appropriate styles.
 * - Sets `aria-busy="true"` during loading state to announce to assistive technology. Pass `isLoading` prop and the component handles ARIA attributes and styles.
 * - Click handlers are blocked when disabled or loading
 * - Keyboard: Enter/Space toggles the pressed state; focus is visible via ring outline
 * - Visible text label provides accessible name — no additional `label` prop needed unlike `ToggleIconButton`
 *
 * ### Tooltip integration
 * ```tsx
 * <Tooltip>
 *   <Tooltip.Trigger>
 *     <ToggleButton pressed={isEnabled} onPressedChange={setIsEnabled}>
 *       Web Search
 *     </ToggleButton>
 *   </Tooltip.Trigger>
 *   <Tooltip.Content>Enable AI to search the web for current information</Tooltip.Content>
 * </Tooltip>
 * ```
 * Unlike `ToggleIconButton`, Tooltip is optional since the visible text provides the accessible name. Use Tooltip when additional context helps explain the action.
 *
 * ### Additional notes
 * - For Spellbook contributors: `toggleButtonVariants` can be used internally to apply toggle button styling to custom components, and is used on `ToggleIconButton` for consistent style reference.
 * - Consider wrapping in `Tooltip` for additional context when the button text alone may not fully explain the action
 */
export const ToggleButton = Object.assign(ToggleButtonBase, {
  LeadingIcon: ToggleButtonLeadingIconSlot,
  TrailingIcon: ToggleButtonTrailingIconSlot,
  Spinner: ToggleButtonSpinnerSlot,
});
